import type { FundOwnershipDetailModel } from 'api';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { waterfallFundSummaryRowConfigProps } from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/FundSummaryRowConfig/waterfallFundSummaryRowConfig.props';
import {
  AggregatedFundOwnershipDetail,
  CflTotalsByFund,
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/FundSummaryRowConfig/WaterfallFundSummaryRowConfig.types';
import {
  WaterfallFundSummaryColumn,
  WaterfallFundSummaryTableData,
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/useFundSummarySheetConfig/useWaterfallFundSummarySheetConfig.types';
import { toNumberOrZero } from 'utilities/toNumberOrZero/toNumberOrZero';

// Builds row config with a generic title row, aggregated data rows, and a totals row
export const WaterfallFundSummaryRowConfig = (
  props: waterfallFundSummaryRowConfigProps
): ICell<AggregatedFundOwnershipDetail, object, WaterfallFundSummaryColumn, WaterfallFundSummaryTableData>[] => {
  const details = props.captable.fund_ownership?.fund_ownership_detail;
  if (!details) {
    return [];
  }
  const related_entity_fod_map =
    details
      ?.filter(fod => fod.related_entity)
      ?.reduce(
        (acc, fod) => {
          if (acc[fod.related_entity as number]) {
            acc[fod.related_entity as number].push(fod);
          } else {
            acc[fod.related_entity as number] = [fod];
          }
          return acc;
        },
        {} as { [key: number]: FundOwnershipDetailModel[] }
      ) || {};

  const map = new Map<string, AggregatedFundOwnershipDetail>();

  details.forEach(detail => {
    // Skip synthetic CFL-only FODs (id === null) — the Waterfall already folds
    // every fund's CFL contribution from captable.cfl_totals_by_fund a few
    // lines down, so counting the synthetic's invested_capital here would
    // double-count the CFL-only securities. Real FODs always carry an integer
    // id, so this filter is a clean discriminator.
    if (detail.id == null) {
      return;
    }
    const isFund = detail.fund != null && detail.fund !== undefined;
    const idNum = detail.fund;
    if (idNum == null) {
      return;
    }
    const key = `${isFund ? 'fund' : 'rel'}_${idNum}`;

    const invested = toNumberOrZero(detail.invested_capital);
    const cash = toNumberOrZero(detail.cash_distribution);
    const shares = toNumberOrZero(detail.shares_distribution);
    const realized = cash + shares;
    const loanVal = toNumberOrZero(detail.loan_value);

    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        id: idNum,
        fund: isFund ? idNum : null,
        related_entity: !isFund ? idNum : null,
        invested_capital: invested.toString(),
        cash_distribution: cash.toString(),
        shares_distribution: shares.toString(),
        realized_value: realized.toString(),
        loan_value: loanVal.toString(),
        related_reference_set:
          detail.entity_reference != null && related_entity_fod_map[detail.entity_reference as unknown as number]
            ? [detail.entity_reference as unknown as number]
            : [],
        // IRR is applied via useWaterfallIrr onChange — do not bake values into row
        // config or the summary sheet rebuilds on every IRR write and loops.
        irr: 0,
      } as AggregatedFundOwnershipDetail);
    } else {
      const nextInvested = toNumberOrZero(existing.invested_capital) + invested;
      const nextCash = toNumberOrZero(existing.cash_distribution) + cash;
      const nextShares = toNumberOrZero(existing.shares_distribution) + shares;
      existing.invested_capital = nextInvested.toString();
      existing.cash_distribution = nextCash.toString();
      existing.shares_distribution = nextShares.toString();
      existing.realized_value = (nextCash + nextShares).toString();
      existing.loan_value = (toNumberOrZero(existing.loan_value) + loanVal).toString();
      // merge entity_reference into related_reference_set, ensuring uniqueness and presence in related_entity_fod_map
      const currentSet = existing.related_reference_set ?? [];
      const ref = detail.entity_reference as unknown as number | null;
      const incoming = ref != null && related_entity_fod_map[ref] ? [ref] : [];
      if (incoming.length) {
        const merged = new Set<number>([...currentSet, ...incoming]);
        existing.related_reference_set = Array.from(merged);
      }
    }
  });

  // Fold CFL totals into the aggregation. Backend ships cfl_totals_by_fund FX-converted
  // to captable_currency. For funds that already have FOD rows we ADD CFL invested/realized
  // onto the existing totals; for CFL-only funds we spawn a synthetic row keyed by fund_${id}
  // with isCflOnlyFund flagged so downstream IRR + parser branches stay explicit.
  const cflTotalsByFund = (props.captable.cfl_totals_by_fund ?? {}) as CflTotalsByFund;
  Object.entries(cflTotalsByFund).forEach(([fundIdStr, totals]) => {
    const fundId = Number(fundIdStr);
    if (!Number.isFinite(fundId)) {
      return;
    }
    const cflInvested = toNumberOrZero(totals?.cfl_invested);
    const cflRealized = toNumberOrZero(totals?.cfl_realized);
    if (cflInvested === 0 && cflRealized === 0) {
      return;
    }
    const key = `fund_${fundId}`;
    const existing = map.get(key);
    if (existing) {
      const nextInvested = toNumberOrZero(existing.invested_capital) + cflInvested;
      const nextRealized = toNumberOrZero(existing.realized_value) + cflRealized;
      existing.invested_capital = nextInvested.toString();
      existing.realized_value = nextRealized.toString();
      // The parser renders previousProceeds directly off realized_value; cash_distribution
      // / shares_distribution are not surfaced individually here. Leave the FOD-derived
      // sub-legs untouched so cash + shares stays a coherent number, even though it now
      // diverges from realized_value (which intentionally carries the CFL augmentation).
    } else {
      map.set(key, {
        id: fundId,
        fund: fundId,
        related_entity: null,
        invested_capital: cflInvested.toString(),
        cash_distribution: cflRealized.toString(),
        shares_distribution: '0',
        realized_value: cflRealized.toString(),
        loan_value: '0',
        related_reference_set: [],
        irr: 0,
        isCflOnlyFund: true,
      } as AggregatedFundOwnershipDetail);
    }
  });

  const aggregated: AggregatedFundOwnershipDetail[] = Array.from(map.values());

  // Build rows: header placeholder + data + totals placeholder
  const rows: ICell<
    AggregatedFundOwnershipDetail,
    object,
    WaterfallFundSummaryColumn,
    WaterfallFundSummaryTableData
  >[] = [];

  // Title/header placeholder row (parser will render column headers)
  rows.push(
    {} as ICell<AggregatedFundOwnershipDetail, object, WaterfallFundSummaryColumn, WaterfallFundSummaryTableData>
  );

  // Data rows
  rows.push(
    ...aggregated.map(
      data =>
        ({ data }) as ICell<
          AggregatedFundOwnershipDetail,
          object,
          WaterfallFundSummaryColumn,
          WaterfallFundSummaryTableData
        >
    )
  );

  // Totals placeholder row (parser will compute totals)
  rows.push(
    {} as ICell<AggregatedFundOwnershipDetail, object, WaterfallFundSummaryColumn, WaterfallFundSummaryTableData>
  );

  return rows;
};
